import React from "react";
import { useMediaQuery } from "@mui/material";
import { truncateAddress, truncateText } from "@/utils/truncateText";
import { ConditionalTooltip } from "@/pages/discountManagement/components/ConditionalTooltip";
import { FlightRefund, FlightRefundStatus } from "@/types";
import dayjs from "dayjs";
import {
  // getCancellationPolicyText,
  getNetworkDisplayName,
} from "@/utils/utils";

interface RefundCardProps {
  refund: FlightRefund;
  onAction: (refund: FlightRefund, status: FlightRefundStatus) => void;
}

const RefundFlightCard: React.FC<RefundCardProps> = ({ refund, onAction }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <div className="w-full rounded-xl bg-white px-3 py-5 text-sm shadow-md">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Booking ID</span>
          <ConditionalTooltip
            text={refund?.flightOrder?.orderId}
            maxLength={20}
            isMobile={isMobile}
          >
            <span className="cursor-pointer font-semibold text-[#1E293B]">
              {truncateText(refund?.flightOrder?.orderId)}
            </span>
          </ConditionalTooltip>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Booking date</span>
          <ConditionalTooltip
            text={refund?.flightOrder?.createdAt}
            maxLength={30}
            isMobile={isMobile}
          >
            <span className="cursor-pointer font-semibold text-[#1E293B]">
              {truncateText(
                dayjs(refund?.flightOrder?.createdAt).format("YYYY-MM-DD HH:mm:ss"),
                30,
              )}
            </span>
          </ConditionalTooltip>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Contact Email</span>
          <ConditionalTooltip
            text={refund?.emailContact}
            maxLength={15}
            isMobile={isMobile}
          >
            <span className="cursor-pointer font-semibold text-[#1E293B]">
              {truncateText(refund?.emailContact, 15)}
            </span>
          </ConditionalTooltip>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Amount of refund</span>
          <span className="font-semibold text-[#1E293B]">
            {/* {!refund?.flightOrder?.totalPaid ? "0" : `${refund?.flightOrder?.totalPaid} USD`} */}
            {[FlightRefundStatus.PENDING_ADMIN, FlightRefundStatus.PENDING_FLINK].includes(refund.status) ? `${refund.refundAmountFlink} USD` : `${refund.refundAmount} USD`}
          </span>
        </div>
        <hr className="bg-button-background" />

        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Network</span>
          <span className="font-semibold text-[#1E293B]">
            {!refund?.network
              ? "-"
              : getNetworkDisplayName(refund?.network)}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Address to refund</span>
          <span className="font-semibold text-[#1E293B]">
            {refund?.walletAddress ? (
              <ConditionalTooltip
                text={refund?.walletAddress.toString()}
                maxLength={10}
                isMobile={isMobile}
              >
                <span className="cursor-pointer">
                  {truncateAddress(refund?.walletAddress.toString())}
                </span>
              </ConditionalTooltip>
            ) : (
              <ConditionalTooltip
                text={refund?.bankName?.toString() || "-"}
                maxLength={10}
                isMobile={isMobile}
              >
                <span className="cursor-pointer">
                  {truncateAddress(refund?.bankAccountNumber?.toString() || "-")}
                </span>
              </ConditionalTooltip>
            )}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Request Date</span>
          <span className="font-semibold text-[#1E293B]">
            {dayjs(refund?.createdAt).format("YYYY/MM/DD")}
          </span>
        </div>

        {/* <div className="flex items-center justify-between">
          <span className="text-[#334155]">Cancellation Policy</span>
          <span className="text-right font-semibold text-[#1E293B]">
            {getCancellationPolicyText(
              refund?.cancelRestrictionType,
              refund?.requestDate || "",
              refund?.checkInDate,
              refund?.cancelRestrictionDay,
              refund?.cancelRestrictionTime,
            )}
          </span>
        </div> */}
      </div>

      <div className="mt-6 flex gap-4">
        {(() => {
          switch (refund?.status.toUpperCase()) {
            case FlightRefundStatus.PENDING_FLINK:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.PENDING_FLINK)}
                  className="flex-1 rounded-lg bg-text px-4 py-2 text-sm font-medium text-white"
                >
                  Flight Review
                </button>
              );
            case FlightRefundStatus.PENDING_USER_CONFIRM:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.PENDING_USER_CONFIRM)}
                  className="flex-1 rounded-lg bg-text px-4 py-2 text-sm font-medium text-white"
                >
                  User Confirm
                </button>
              );  
            case FlightRefundStatus.PENDING_ADMIN:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.PENDING_ADMIN)}
                  className="flex-1 rounded-lg bg-text px-4 py-2 text-sm font-medium text-white"
                >
                  Review
                </button>
              );
            case FlightRefundStatus.USER_APPROVED:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.USER_APPROVED)}
                  className="flex-1 rounded-lg bg-text px-4 py-2 text-sm font-medium text-white"
                >
                  Complete Refund
                </button>
              );
            case FlightRefundStatus.REJECTED:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.REJECTED)}
                  className="flex-1 rounded-lg bg-[#F75C5C] px-4 py-2 text-sm font-medium text-white"
                >
                  Reject
                </button>
              );
            case FlightRefundStatus.COMPLETED:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.COMPLETED)}
                  className="flex-1 rounded-lg bg-text px-4 py-2 text-sm font-medium text-white"
                >
                  {refund?.transactionId ? "Completed" : "Complete Refund"}
                </button>
              );
            case FlightRefundStatus.TIMEOUT_CLOSED:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.TIMEOUT_CLOSED)}
                  className="flex-1 rounded-lg bg-text px-4 py-2 text-sm font-medium text-white"
                >
                  Timeout
                </button>
              );
            case FlightRefundStatus.FAILED:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.FAILED)}
                  className="flex-1 rounded-lg bg-[#F75C5C] px-4 py-2 text-sm font-medium text-white"
                >
                  Failed
                </button>
              );
            default:
              return (
                <button
                  onClick={() => onAction(refund, FlightRefundStatus.PENDING_ADMIN)}
                  className="flex-1 rounded-lg bg-text px-4 py-2 text-sm font-medium text-white"
                >
                  Review
                </button>
              );
          }
        })()}
      </div>
    </div>
  );
};

export default RefundFlightCard;
